import axios from 'axios'

export const OPENAI_API_KEY = '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'

// ─── GPT-4o API call ─────────────────────────────────────────────────────────
const callGPT4o = async (messages) => {
  const res = await axios.post(
    'https://api.openai.com/v1/chat/completions',
    { model: 'gpt-4o', messages, max_tokens: 800, temperature: 0.2 },
    { headers: { Authorization: `Bearer ${OPENAI_API_KEY}`, 'Content-Type': 'application/json' } }
  )
  return res.data.choices[0].message.content.trim()
}

// ─── Extract flow steps from abstract ────────────────────────────────────────
const extractFlowSteps = async (abstractText) => {
  const prompt = `You are a scientific diagram expert.

Given the research abstract below, extract exactly 4 flow steps that best represent the research process or key concepts. These will be shown as circular icons in a horizontal flow diagram.
For each step, provide a representative emoji, a unicode symbol, or a short text (1-3 chars, like "Zn" or "pH") to act as the icon inside the circle.

Return ONLY a valid JSON object with no markdown:
{
  "title": "Concise research title (max 10 words)",
  "steps": [
    { 
      "label": "2-4 word step label", 
      "sublabel": "one additional detail word or symbol (optional, max 2 words)",
      "icon": "🌾"
    }
  ],
  "caption": "One sentence describing the flow diagram"
}

Produce exactly 4 steps in the "steps" array.

Abstract:
${abstractText}

OUTPUT: Only the JSON object.`

  const raw = await callGPT4o([{ role: 'user', content: prompt }])
  const cleaned = raw.replace(/```[a-zA-Z]*/g, '').trim()
  return JSON.parse(cleaned)
}

// ─── Wrap text centered ───────────────────────────────────────────────────────
const wrapTextCentered = (ctx, text, cx, startY, maxW, lineH) => {
  const words = text.split(' ')
  const lines = []
  let current = ''
  for (const word of words) {
    const test = current ? current + ' ' + word : word
    if (ctx.measureText(test).width > maxW && current) {
      lines.push(current)
      current = word
    } else {
      current = test
    }
  }
  if (current) lines.push(current)
  lines.forEach((line, i) => {
    ctx.fillText(line, cx, startY + i * lineH)
  })
  return startY + lines.length * lineH
}

// ─── Build the flow diagram canvas ───────────────────────────────────────────
const buildFlowCanvas = async (data) => {
  const n = data.steps.length
  const W = 1024
  const H = 340

  // Dynamic sizing based on step count
  const CIRCLE_R = n <= 3 ? 60 : n <= 4 ? 40 : 40
  const ARROW_W = n <= 3 ? 100 : n <= 4 ? 80 : 60
  const TOTAL_ITEM_W = CIRCLE_R * 2
  const TOTAL_CONTENT_W = n * TOTAL_ITEM_W + (n - 1) * ARROW_W
  const START_X = (W - TOTAL_CONTENT_W) / 2 + CIRCLE_R
  const CENTER_Y = 135

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')

  // ── Background ──────────────────────────────────────────────────────────
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, W, H)

  // Subtle top accent bar
  const hdr = ctx.createLinearGradient(0, 0, W, 0)
  hdr.addColorStop(0, '#2e7d87')
  hdr.addColorStop(1, '#4a90d9')
  ctx.fillStyle = hdr
  ctx.fillRect(0, 0, W, 6)

  // ── Title ───────────────────────────────────────────────────────────────
  ctx.fillStyle = '#1a3a5c'
  ctx.font = `bold ${n <= 4 ? 14 : 15}px Arial, Helvetica, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.fillText(data.title, W / 2, 18)

  // ── Draw each step ──────────────────────────────────────────────────────
  const BORDER_COLOR = '#2e7d87'
  const BORDER_W = 5
  const SHADOW_COLOR = 'rgba(46,125,135,0.18)'

  for (let i = 0; i < n; i++) {
    const cx = START_X + i * (TOTAL_ITEM_W + ARROW_W)
    const cy = CENTER_Y
    const step = (data.steps && data.steps[i]) || { label: `Step ${i + 1}`, sublabel: '', icon: '📈' }

    // Shadow
    ctx.save()
    ctx.shadowColor = SHADOW_COLOR
    ctx.shadowBlur = 18
    ctx.beginPath()
    ctx.arc(cx, cy, CIRCLE_R + BORDER_W, 0, Math.PI * 2)
    ctx.fillStyle = BORDER_COLOR
    ctx.fill()
    ctx.restore()

    // Outer ring (teal)
    ctx.beginPath()
    ctx.arc(cx, cy, CIRCLE_R + BORDER_W, 0, Math.PI * 2)
    ctx.fillStyle = BORDER_COLOR
    ctx.fill()

    // Inner white gap
    ctx.beginPath()
    ctx.arc(cx, cy, CIRCLE_R + 1, 0, Math.PI * 2)
    ctx.fillStyle = '#FFFFFF'
    ctx.fill()

    // Fill circle background
    ctx.beginPath()
    ctx.arc(cx, cy, CIRCLE_R - 2, 0, Math.PI * 2)
    ctx.fillStyle = '#f0f7f7'
    ctx.fill()

    // Draw text/emoji icon
    if (step.icon) {
      ctx.fillStyle = '#1a3a5c'
      ctx.font = `bold ${CIRCLE_R * 0.8}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Segoe UI Symbol", sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(step.icon, cx, cy)
    }

    // ── Labels below circle ───────────────────────────────────────────────
    const labelY = cy + CIRCLE_R + BORDER_W + 18

    ctx.fillStyle = '#1a3a5c'
    ctx.font = `bold ${n <= 4 ? 14 : 16}px Arial, Helvetica, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    const nextY = wrapTextCentered(ctx, step.label, cx, labelY, TOTAL_ITEM_W + 80, 22)

    if (step.sublabel) {
      ctx.fillStyle = '#2e7d87'
      ctx.font = `italic ${n <= 4 ? 15 : 13}px Arial, Helvetica, sans-serif`
      ctx.fillText(step.sublabel, cx, nextY)
    }

    // ── Arrow to next step ────────────────────────────────────────────────
    if (i < n - 1) {
      const arrowX1 = cx + CIRCLE_R + BORDER_W + 6
      const arrowX2 = cx + TOTAL_ITEM_W + ARROW_W - CIRCLE_R - BORDER_W - 6
      const ay = cy

      // Arrow line
      ctx.beginPath()
      ctx.moveTo(arrowX1, ay)
      ctx.lineTo(arrowX2, ay)
      ctx.strokeStyle = '#4a90d9'
      ctx.lineWidth = 2.5
      ctx.setLineDash([])
      ctx.stroke()

      // Arrowhead
      const HEAD = 10
      ctx.beginPath()
      ctx.moveTo(arrowX2, ay)
      ctx.lineTo(arrowX2 - HEAD, ay - HEAD * 0.5)
      ctx.lineTo(arrowX2 - HEAD, ay + HEAD * 0.5)
      ctx.closePath()
      ctx.fillStyle = '#4a90d9'
      ctx.fill()
    }
  }

  // ── Caption ─────────────────────────────────────────────────────────────
  if (data.caption) {
    ctx.fillStyle = '#2e7d87'
    ctx.font = `italic 11px Arial, Helvetica, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.fillText(`Figure: ${data.caption}`, W / 2, H - 10)
  }

  // Bottom accent bar
  const ftr = ctx.createLinearGradient(0, 0, W, 0)
  ftr.addColorStop(0, '#2e7d87')
  ftr.addColorStop(1, '#4a90d9')
  ctx.fillStyle = ftr
  ctx.fillRect(0, H - 4, W, 4)

  return canvas
}

// ─── Main export ─────────────────────────────────────────────────────────────
export const generateGraphicalAbstract = async (abstractText) => {
  try {
    const n = 4

    // Step 1 — Extract flow steps from GPT-4o
    console.log('Extracting flow steps from abstract...')
    let data
    try {
      data = await extractFlowSteps(abstractText)
      console.log('Flow data:', data)
    } catch (e) {
      console.warn('JSON extraction failed, using fallback.', e.message)
      data = {
        title: 'Research Flow Diagram',
        steps: Array.from({ length: 4 }).map((_, i) => ({ label: `Step ${i + 1}`, sublabel: '', icon: '📈' })),
        caption: 'Schematic of the research methodology and key findings.'
      }
    }

    // Ensure steps array length is correct
    while (data.steps.length < n) {
      data.steps.push({ label: `Step ${data.steps.length + 1}`, sublabel: '', icon: '📈' })
    }

    // Step 2 — Build canvas
    const canvas = await buildFlowCanvas(data)

    // Step 3 — Export PNG
    const pngBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/png')
    })

    const generatedFile = new File([pngBlob], 'graphical_abstract.png', { type: 'image/png' })
    return { file: generatedFile, url: URL.createObjectURL(pngBlob) }

  } catch (error) {
    console.error('Error generating graphical abstract:', error)
    throw error
  }
}
